import { MatchQualityTier } from '@opencare/shared-types/models/match/MatchQualityTier';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectDataMatchQualityTier } from 'connect/selectors';
import { DROPOFF_SURVEY_TYPEFORM_ID, TypeformModalStateHandler } from 'connect/state/handlers/typeform-modal';

const INACTIVITY_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'touchstart', 'scroll'];

export enum DropoffSurveySource {
  Results = 'results',
  Checkout = 'checkout',
}

interface UseDropoffSurveyOptions {
  source: DropoffSurveySource;
  checkoutStep?: string;
  disabled?: boolean;
}

/**
 * Hook that manages the drop-off survey (Typeform popup) triggered by user inactivity.
 *
 * @param source - Where the survey is triggered from ('results' | 'checkout'). Sent to Typeform as a hidden field.
 * @param checkoutStep - Optional checkout step ID (e.g. 'account', 'insurance'). Sent to Typeform as a hidden field.
 * @param disabled - Optionally disable the survey (e.g. on the verification step).
 *
 * Returns:
 * - `typeformRef`: ref to attach to the Typeform `PopupButton` via `embedRef`
 * - `hiddenFields`: object with `source` and `step` to pass to the `PopupButton` hidden fields
 */
export const useDropoffSurvey = ({
  source,
  checkoutStep,
  disabled = false,
}: UseDropoffSurveyOptions) => {
  const dispatch = useDispatch();
  const typeformRef = useRef<{ open: () => void; unmount: () => void; } | undefined>(undefined);

  const typeformModalData = useSelector<any, { hasShown?: string[]; } | undefined>(TypeformModalStateHandler.selector);
  const matchQualityTier = useSelector(selectDataMatchQualityTier);

  const hasAlreadyShown = typeformModalData?.hasShown?.includes(DROPOFF_SURVEY_TYPEFORM_ID);

  const isTierA = matchQualityTier === MatchQualityTier.A;
  const shouldActivate = !disabled && !hasAlreadyShown && isTierA;

  const openSurvey = useCallback(() => {
    if (!typeformRef.current) {
      return;
    }

    const previouslyShown = typeformModalData?.hasShown ?? [];
    const updatedHasShown = Array.from(new Set([...previouslyShown, DROPOFF_SURVEY_TYPEFORM_ID]));

    dispatch(TypeformModalStateHandler.update({
      hasShown: updatedHasShown,
    }));
    typeformRef.current.open();
  }, [dispatch, typeformModalData]);

  useEffect(() => {
    if (!shouldActivate) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(openSurvey, INACTIVITY_TIMEOUT_MS);
    };

    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, resetTimer));

    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, resetTimer));
    };
  }, [shouldActivate, openSurvey]);

  const hiddenFields = useMemo<Record<string, string>>(
    () => ({ source, ...(checkoutStep && { step: checkoutStep }) }),
    [source, checkoutStep],
  );

  return { typeformRef, hiddenFields };
};
